import React, { useRef, useEffect } from 'react'
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion'

const CONNECT_DIST = 120

/**
 * Canvas-based constellation animation.
 * Reads --p from the nearest .section ancestor to drive a brightness wave.
 * @param {React.RefObject} sectionRef - optional explicit section ref to read --p from
 */
export function ConstellationCanvas({ sectionRef, className = '', style }) {
    const canvasRef = useRef(null)
    const reducedMotion = usePrefersReducedMotion()

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas || reducedMotion) return

        const ctx = canvas.getContext('2d')
        const dpr = Math.min(window.devicePixelRatio || 1, 2)
        const isTouch = window.matchMedia('(hover: none), (pointer: coarse)').matches

        let w = 0, h = 0, points = [], rafId
        const mouse = { x: -1000, y: -1000, active: false }

        function resize() {
            const r = canvas.getBoundingClientRect()
            w = r.width; h = r.height
            canvas.width = w * dpr; canvas.height = h * dpr
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
            seed()
        }

        function seed() {
            const count = isTouch ? 80 : Math.min(200, Math.floor((w * h) / 8000))
            points = Array.from({ length: count }, () => ({
                x: Math.random() * w,
                y: Math.random() * h,
                vx: (Math.random() - 0.5) * 0.12,
                vy: (Math.random() - 0.5) * 0.12,
                r: 0.6 + Math.random() * 1.4,
                phase: Math.random() * Math.PI * 2,
            }))
        }

        function getP() {
            const el = sectionRef?.current || canvas.closest('.section')
            if (!el) return 0
            return parseFloat(el.style.getPropertyValue('--p')) || 0
        }

        const onMove = (e) => {
            const r = canvas.getBoundingClientRect()
            mouse.x = e.clientX - r.left
            mouse.y = e.clientY - r.top
            mouse.active = true
        }
        const onLeave = () => { mouse.active = false }

        canvas.addEventListener('mousemove', onMove)
        canvas.addEventListener('mouseleave', onLeave)

        let frame = 0
        function draw() {
            ctx.clearRect(0, 0, w, h)
            const p = getP()
            const wave = h * (p * 1.4 - 0.2)
            frame++

            for (const pt of points) {
                pt.x += pt.vx; pt.y += pt.vy
                if (pt.x < 0 || pt.x > w) pt.vx *= -1
                if (pt.y < 0 || pt.y > h) pt.vy *= -1

                const dist = Math.abs(pt.y - wave)
                const beam = Math.max(0, 1 - dist / 200)
                const pulse = 0.5 + 0.5 * Math.sin(frame * 0.018 + pt.phase)
                const alpha = 0.18 + beam * 0.55 + pulse * 0.06

                ctx.beginPath()
                ctx.arc(pt.x, pt.y, pt.r, 0, Math.PI * 2)
                ctx.fillStyle = `rgba(245,245,247,${alpha.toFixed(3)})`
                ctx.fill()
            }

            // connections
            for (let i = 0; i < points.length; i++) {
                for (let j = i + 1; j < points.length; j++) {
                    const dx = points[i].x - points[j].x
                    const dy = points[i].y - points[j].y
                    const d = Math.sqrt(dx * dx + dy * dy)
                    if (d < CONNECT_DIST) {
                        ctx.beginPath()
                        ctx.moveTo(points[i].x, points[i].y)
                        ctx.lineTo(points[j].x, points[j].y)
                        ctx.strokeStyle = `rgba(245,245,247,${(0.06 * (1 - d / CONNECT_DIST)).toFixed(3)})`
                        ctx.lineWidth = 0.5
                        ctx.stroke()
                    }
                }
            }

            rafId = requestAnimationFrame(draw)
        }

        const ro = new ResizeObserver(resize)
        ro.observe(canvas)
        resize()
        rafId = requestAnimationFrame(draw)

        return () => {
            canvas.removeEventListener('mousemove', onMove)
            canvas.removeEventListener('mouseleave', onLeave)
            ro.disconnect()
            cancelAnimationFrame(rafId)
        }
    }, [sectionRef, reducedMotion])

    return (
        <canvas
            ref={canvasRef}
            className={`constellation-canvas ${className}`}
            style={{ display: 'block', width: '100%', height: '100%', ...style }}
            aria-hidden="true"
        />
    )
}
